import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import {
  Alert,
  App,
  Button,
  Checkbox,
  Form,
  Input,
  Radio,
  Select,
  Skeleton,
  Space,
  Spin,
  Typography,
} from 'antd';
import { Braces, ChevronLeft, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  iamApi as iamHttpApi,
  type CreatePolicyRequest,
  type IamPolicyConfigStatement,
  type ListActionResourcesResponse,
  type ListActionsResponse,
} from '@/api/iam.api';
import { useCreateIamPolicy, useUpdateIamPolicy } from '@/pages/iam/composables/useIamSupportData';
import type {
  IamEffect,
  IamPolicyStatement,
  PermissionAction,
} from '@/pages/iam/composables/usePolicyPermissionItems';
import './policies.css';

type PolicyEditorMode = 'visual' | 'json';

interface VisualStatementForm extends IamPolicyStatement {
  service?: string;
}

interface PolicyFormValues {
  name: string;
  description?: string;
  visualStatements: VisualStatementForm[];
  json: string;
}

const INITIAL_STATEMENTS: IamPolicyStatement[] = [
  { effect: 'Allow', actions: [], resourceScope: 'OWN' },
];

const REFERENCE_QUERY_BEHAVIOR = {
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: 'always',
  staleTime: 0,
} as const;

function waitForEditorLoadingPaint() {
  return new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });
}

function actionResourcesQueryOptions() {
  return {
    queryKey: ['iam', 'policy-create', 'action-resources'] as const,
    queryFn: async () => {
      const response = await iamHttpApi.listActionResources();
      if (response.code !== 0) {
        throw new Error(response.message || '获取服务列表失败');
      }
      return response;
    },
    ...REFERENCE_QUERY_BEHAVIOR,
  };
}

function actionQueryOptions(groupValue: string) {
  return {
    queryKey: ['iam', 'policy-create', 'actions', groupValue] as const,
    queryFn: async () => {
      const response = await iamHttpApi.listActions(groupValue);
      if (response.code !== 0) {
        throw new Error(response.message || '获取权限点失败');
      }
      return response;
    },
    ...REFERENCE_QUERY_BEHAVIOR,
  };
}

function mergePermissionActionCatalogs(...catalogs: PermissionAction[][]): PermissionAction[] {
  const actionsByCode = new Map<string, PermissionAction>();
  catalogs.flat().forEach((action) => actionsByCode.set(action.code, action));
  return [...actionsByCode.values()];
}

function toPolicyServiceOptions(response: ListActionResourcesResponse) {
  return response.data.list.map((service) => ({
    label: service.group,
    value: service.group_value,
  }));
}

function toPermissionActions(response: ListActionsResponse): PermissionAction[] {
  const collections: Array<{
    actionGroup: PermissionAction['group'];
    actions: ListActionsResponse['data']['write_actions']['actions'];
  }> = [
    { actionGroup: '读操作', actions: response.data.read_items.actions },
    { actionGroup: '写操作', actions: response.data.write_actions.actions },
    { actionGroup: '列表操作', actions: response.data.list_items.actions },
    { actionGroup: '写操作', actions: response.data.permission_items.actions },
  ];

  return collections.flatMap(({ actionGroup, actions }) =>
    actions.map((action) => ({
      code: action.code,
      service: action.group_value,
      serviceLabel: action.group,
      serviceName: action.service_name,
      group: actionGroup,
      name: action.display_name,
      description: action.description,
    }))
  );
}

function toEditorStatement(statement: IamPolicyConfigStatement): IamPolicyStatement {
  const normalizedEffect = statement.effect.toLowerCase();
  if (normalizedEffect !== 'allow' && normalizedEffect !== 'deny') {
    throw new Error(`不支持的策略 Effect：${statement.effect}`);
  }
  return {
    effect: normalizedEffect === 'deny' ? 'Deny' : 'Allow',
    actions: statement.action,
    resourceScope:
      statement.condition?.string_equals?.['inf:resourceOwner'] === '{self}' ? 'OWN' : 'ALL',
  };
}

function toPolicyConfig(statements: IamPolicyStatement[]): CreatePolicyRequest['policy_config'] {
  return {
    version: '1',
    statement: statements.map((statement) => ({
      effect: statement.effect,
      action: [...statement.actions],
      ...(statement.resourceScope === 'OWN'
        ? {
            condition: {
              string_equals: {
                'inf:resourceOwner': '{self}',
              },
            },
          }
        : {}),
    })),
  };
}

function toVisualStatements(
  statements: IamPolicyStatement[],
  actions: PermissionAction[]
): Array<IamPolicyStatement & { service: string }> {
  return statements.flatMap((statement) => {
    const matchingActions = statement.actions.flatMap((code) => {
      if (code === '*') return actions;
      const wildcardService = code.match(/^([^:]+):\*\*$/)?.[1];
      if (wildcardService) {
        return actions.filter(
          (action) =>
            action.serviceName === wildcardService || action.code.startsWith(`${wildcardService}:`)
        );
      }
      const action = actions.find((candidate) => candidate.code === code);
      return action ? [action] : [];
    });
    const actionsByService = new Map<string, Set<string>>();
    matchingActions.forEach((action) => {
      const codes = actionsByService.get(action.service) || new Set<string>();
      codes.add(action.code);
      actionsByService.set(action.service, codes);
    });
    return [...actionsByService].map(([service, codes]) => ({
      ...statement,
      service,
      actions: [...codes],
    }));
  });
}

/**
 * TODO(IAM contract): 以下 JSON v1 校验暂时与创建页共置，等待后端策略校验契约确认：
 * - JSON 编辑模式是否继续保留客户端完整校验；
 * - `*` / `<service>:**` 的通配语义与可组合规则；
 * - condition 支持的操作符、键和值；
 * - 服务端校验错误如何映射到具体 statement 路径。
 */
interface IamPolicyValidationIssue {
  path: string;
  code:
    | 'INVALID_DOCUMENT'
    | 'INVALID_VERSION'
    | 'INVALID_STATEMENT'
    | 'INVALID_EFFECT'
    | 'INVALID_ACTION'
    | 'UNKNOWN_ACTION'
    | 'MULTIPLE_SERVICES'
    | 'INVALID_CONDITION';
  message: string;
}

interface IamRamPolicyStatementV1 {
  effect: IamEffect;
  action: string[];
  condition?: {
    string_equals: {
      'inf:resourceOwner': '{self}';
    };
  };
}

type ParseIamPolicyDocumentResult =
  | { ok: true; statements: IamPolicyStatement[] }
  | { ok: false; issues: IamPolicyValidationIssue[] };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function issue(
  path: string,
  code: IamPolicyValidationIssue['code'],
  message: string
): IamPolicyValidationIssue {
  return { path, code, message };
}

function unsupportedKeys(value: Record<string, unknown>, allowedKeys: string[]): string[] {
  const allowed = new Set(allowedKeys);
  return Object.keys(value).filter((key) => !allowed.has(key));
}

function isOwnerCondition(
  value: unknown
): value is NonNullable<IamRamPolicyStatementV1['condition']> {
  if (!isRecord(value) || unsupportedKeys(value, ['string_equals']).length > 0) {
    return false;
  }
  const stringEquals = value.string_equals;
  return (
    isRecord(stringEquals) &&
    unsupportedKeys(stringEquals, ['inf:resourceOwner']).length === 0 &&
    stringEquals['inf:resourceOwner'] === '{self}'
  );
}

function serviceForAction(action: string, actions: PermissionAction[]) {
  if (action === '*') return '*';
  const wildcardService = action.match(/^([^:]+):\*\*$/)?.[1];
  if (wildcardService) {
    return actions.some(
      (item) => item.serviceName === wildcardService || item.code.startsWith(`${wildcardService}:`)
    )
      ? `wildcard:${wildcardService}`
      : undefined;
  }
  return actions.find((item) => item.code === action)?.service;
}

function parsePolicyStatement(
  value: unknown,
  index: number,
  actions: PermissionAction[]
): { statement?: IamRamPolicyStatementV1; issues: IamPolicyValidationIssue[] } {
  const path = `statement[${index}]`;
  if (!isRecord(value)) {
    return { issues: [issue(path, 'INVALID_STATEMENT', 'statement 项必须是对象')] };
  }

  const issues: IamPolicyValidationIssue[] = [];
  const extraFields = unsupportedKeys(value, ['effect', 'action', 'condition']);
  if (extraFields.length > 0) {
    issues.push(
      issue(path, 'INVALID_STATEMENT', `statement 包含不支持的字段：${extraFields.join('、')}`)
    );
  }
  const effect = value.effect;
  if (effect !== 'Allow' && effect !== 'Deny') {
    issues.push(issue(`${path}.effect`, 'INVALID_EFFECT', 'effect 仅支持 Allow 或 Deny'));
  }

  const actionValues = value.action;
  const rawActions = Array.isArray(actionValues) ? actionValues : [];
  const validActionShape =
    Array.isArray(actionValues) &&
    actionValues.length > 0 &&
    actionValues.every((item) => typeof item === 'string' && item.trim());
  if (!validActionShape) {
    issues.push(issue(`${path}.action`, 'INVALID_ACTION', 'action 必须是非空字符串数组'));
  }
  const normalizedActions = [
    ...new Set(
      rawActions
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean)
    ),
  ];
  if (
    normalizedActions.some((action) => action === '*' || /^([^:]+):\*\*$/.test(action)) &&
    normalizedActions.length > 1
  ) {
    issues.push(issue(`${path}.action`, 'INVALID_ACTION', '通配权限点必须单独配置'));
  }

  const unknownActions = normalizedActions.filter((action) => !serviceForAction(action, actions));
  unknownActions.forEach((action) => {
    issues.push(issue(`${path}.action`, 'UNKNOWN_ACTION', `未注册的权限点：${action}`));
  });
  const services = new Set(
    normalizedActions.map((action) => serviceForAction(action, actions)).filter(Boolean)
  );
  if (services.size > 1) {
    issues.push(
      issue(`${path}.action`, 'MULTIPLE_SERVICES', '一条 statement 只能包含一个服务的权限点')
    );
  }

  const condition = value.condition;
  const isOwnResource = isOwnerCondition(condition);
  if (condition !== undefined && !isOwnResource) {
    issues.push(
      issue(
        `${path}.condition`,
        'INVALID_CONDITION',
        'condition 仅支持 string_equals.inf:resourceOwner = "{self}"；全部资源请省略 condition'
      )
    );
  }

  if (issues.length || (effect !== 'Allow' && effect !== 'Deny')) {
    return { issues };
  }
  return {
    statement: {
      effect,
      action: normalizedActions,
      ...(isOwnResource
        ? {
            condition: {
              string_equals: {
                'inf:resourceOwner': '{self}' as const,
              },
            },
          }
        : {}),
    },
    issues,
  };
}

function parseIamPolicyDocumentV1(
  input: unknown,
  actions: PermissionAction[]
): ParseIamPolicyDocumentResult {
  if (!isRecord(input)) {
    return { ok: false, issues: [issue('$', 'INVALID_DOCUMENT', '策略 JSON 必须是对象')] };
  }

  const issues: IamPolicyValidationIssue[] = [];
  const extraFields = unsupportedKeys(input, ['version', 'statement']);
  if (extraFields.length > 0) {
    issues.push(
      issue('$', 'INVALID_DOCUMENT', `policy_config 包含不支持的字段：${extraFields.join('、')}`)
    );
  }
  if (input.version !== '1') {
    issues.push(issue('version', 'INVALID_VERSION', 'version 必须固定为字符串 "1"'));
  }
  if (!Array.isArray(input.statement) || !input.statement.length) {
    issues.push(issue('statement', 'INVALID_STATEMENT', 'statement 必须是非空数组'));
  }

  const statements: IamRamPolicyStatementV1[] = [];
  if (Array.isArray(input.statement)) {
    input.statement.forEach((statement, index) => {
      const parsed = parsePolicyStatement(statement, index, actions);
      issues.push(...parsed.issues);
      if (parsed.statement) statements.push(parsed.statement);
    });
  }
  if (issues.length) return { ok: false, issues };

  return {
    ok: true,
    statements: statements.map((statement) => ({
      effect: statement.effect,
      actions: [...statement.action],
      resourceScope: statement.condition ? 'OWN' : 'ALL',
    })),
  };
}

function validatePolicyJson(value: string | undefined, actions: PermissionAction[]) {
  try {
    const parsed = parseIamPolicyDocumentV1(JSON.parse(value || ''), actions);
    if (!parsed.ok) {
      return {
        errors: parsed.issues.map((issue) => `${issue.path}：${issue.message}`),
        statements: undefined,
      };
    }
    return { errors: [] as string[], statements: parsed.statements };
  } catch (error) {
    return {
      errors: [`JSON 语法：${error instanceof Error ? error.message : '无法解析当前内容'}`],
      statements: undefined,
    };
  }
}

interface PolicyActionSelectorProps {
  actions: PermissionAction[];
  value?: string[];
  onChange?: (value: string[]) => void;
}

interface PolicyActionItemProps {
  action: PermissionAction;
  checked: boolean;
  onToggle: (code: string, checked: boolean) => void;
}

const POLICY_ACTION_GROUPS = ['读操作', '写操作', '列表操作'] as const;
const ACTION_ROW_HEIGHT = 76;
const ACTION_ROW_GAP = 12;
const ACTION_OVERSCAN_PX = 240;

const PolicyActionItem = memo(function PolicyActionItem({
  action,
  checked,
  onToggle,
}: PolicyActionItemProps) {
  return (
    <Checkbox
      className="iam-policy-action-item"
      checked={checked}
      onChange={(event) => onToggle(action.code, event.target.checked)}
    >
      <span className="iam-policy-action-copy">
        <span title={action.name}>{action.name}</span>
        <code title={action.code}>{action.code}</code>
      </span>
    </Checkbox>
  );
});

interface VirtualActionGridProps {
  actions: PermissionAction[];
  selected: Set<string>;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  onToggle: (code: string, checked: boolean) => void;
}

function getActionGridColumnCount(width: number): number {
  if (width < 560) return 1;
  if (width < 900) return 2;
  return 3;
}

const VirtualActionGrid = memo(function VirtualActionGrid({
  actions,
  selected,
  scrollContainerRef,
  onToggle,
}: VirtualActionGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | undefined>(undefined);
  const [viewport, setViewport] = useState({ startRow: 0, endRow: 0, columnCount: 3 });

  const updateViewport = useCallback(() => {
    const grid = gridRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!grid || !scrollContainer) return;

    const columnCount = getActionGridColumnCount(grid.clientWidth);
    const rowCount = Math.ceil(actions.length / columnCount);
    const rowStride = ACTION_ROW_HEIGHT + ACTION_ROW_GAP;
    const gridRect = grid.getBoundingClientRect();
    const scrollRect = scrollContainer.getBoundingClientRect();
    const visibleTop = scrollRect.top - gridRect.top - ACTION_OVERSCAN_PX;
    const visibleBottom = scrollRect.bottom - gridRect.top + ACTION_OVERSCAN_PX;
    const startRow = Math.min(rowCount, Math.max(0, Math.floor(visibleTop / rowStride)));
    const endRow = Math.max(startRow, Math.min(rowCount, Math.ceil(visibleBottom / rowStride)));

    setViewport((current) =>
      current.startRow === startRow &&
      current.endRow === endRow &&
      current.columnCount === columnCount
        ? current
        : { startRow, endRow, columnCount }
    );
  }, [actions.length, scrollContainerRef]);

  const scheduleViewportUpdate = useCallback(() => {
    if (frameRef.current !== undefined) return;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = undefined;
      updateViewport();
    });
  }, [updateViewport]);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!grid || !scrollContainer) return;

    let listeningForScroll = false;
    const startListeningForScroll = () => {
      if (listeningForScroll) return;
      listeningForScroll = true;
      scrollContainer.addEventListener('scroll', scheduleViewportUpdate, { passive: true });
      scheduleViewportUpdate();
    };
    const stopListeningForScroll = () => {
      if (!listeningForScroll) return;
      listeningForScroll = false;
      scrollContainer.removeEventListener('scroll', scheduleViewportUpdate);
    };

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startListeningForScroll();
        } else {
          stopListeningForScroll();
          setViewport((current) => ({ ...current, startRow: 0, endRow: 0 }));
        }
      },
      { root: scrollContainer, rootMargin: `${ACTION_OVERSCAN_PX}px 0px` }
    );
    updateViewport();
    intersectionObserver.observe(grid);

    const resizeObserver = new ResizeObserver(scheduleViewportUpdate);
    resizeObserver.observe(grid);
    resizeObserver.observe(scrollContainer);

    return () => {
      stopListeningForScroll();
      intersectionObserver.disconnect();
      resizeObserver.disconnect();
      if (frameRef.current !== undefined) window.cancelAnimationFrame(frameRef.current);
    };
  }, [scheduleViewportUpdate, scrollContainerRef, updateViewport]);

  const rowCount = Math.ceil(actions.length / viewport.columnCount);
  const totalHeight = rowCount * ACTION_ROW_HEIGHT + Math.max(0, rowCount - 1) * ACTION_ROW_GAP;
  const visibleRows = Array.from(
    { length: Math.max(0, viewport.endRow - viewport.startRow) },
    (_, index) => viewport.startRow + index
  );

  return (
    <div
      ref={gridRef}
      className="iam-policy-action-grid"
      style={{ height: totalHeight } as CSSProperties}
    >
      {visibleRows.map((rowIndex) => {
        const rowActions = actions.slice(
          rowIndex * viewport.columnCount,
          (rowIndex + 1) * viewport.columnCount
        );
        return (
          <div
            className="iam-policy-action-row"
            key={rowIndex}
            style={{
              top: rowIndex * (ACTION_ROW_HEIGHT + ACTION_ROW_GAP),
              gridTemplateColumns: `repeat(${viewport.columnCount}, minmax(0, 1fr))`,
            }}
          >
            {rowActions.map((action) => (
              <PolicyActionItem
                key={action.code}
                action={action}
                checked={selected.has(action.code)}
                onToggle={onToggle}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
});

function PolicyActionSelector({
  actions,
  value = [],
  onChange,
  scrollContainerRef,
}: PolicyActionSelectorProps & { scrollContainerRef: RefObject<HTMLDivElement | null> }) {
  const groupedActions = useMemo(
    () =>
      POLICY_ACTION_GROUPS.map((group) => ({
        group,
        actions: actions.filter((action) => action.group === group),
      })).filter(({ actions: groupActions }) => groupActions.length > 0),
    [actions]
  );
  const selected = useMemo(() => new Set(value), [value]);
  const allSelected = actions.length > 0 && actions.every((action) => selected.has(action.code));
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  onChangeRef.current = onChange;

  const toggleCodes = useCallback((codes: string[], checked: boolean) => {
    const next = new Set(valueRef.current);
    codes.forEach((code) => (checked ? next.add(code) : next.delete(code)));
    const nextValue = [...next];
    valueRef.current = nextValue;
    onChangeRef.current?.(nextValue);
  }, []);

  const toggleAction = useCallback(
    (code: string, checked: boolean) => toggleCodes([code], checked),
    [toggleCodes]
  );

  return (
    <div className="iam-policy-action-selector">
      <div className="iam-policy-actions-toolbar">
        <Typography.Text type="secondary">
          已选择 {value.length} / {actions.length} 个权限点
        </Typography.Text>
        <Button
          type="link"
          size="small"
          onClick={() =>
            toggleCodes(
              actions.map((action) => action.code),
              !allSelected
            )
          }
        >
          {allSelected ? '取消全选' : '全选当前服务'}
        </Button>
      </div>
      <div className="iam-policy-action-groups">
        {groupedActions.map(({ group, actions: groupActions }) => {
          const groupSelected = groupActions.every((action) => selected.has(action.code));
          return (
            <div className="iam-policy-action-group" key={group}>
              <div className="iam-policy-action-group-head">
                <Typography.Text strong>{group}</Typography.Text>
                <Button
                  type="link"
                  size="small"
                  onClick={() =>
                    toggleCodes(
                      groupActions.map((action) => action.code),
                      !groupSelected
                    )
                  }
                >
                  {groupSelected ? '取消' : '全选'}
                </Button>
              </div>
              <VirtualActionGrid
                actions={groupActions}
                selected={selected}
                scrollContainerRef={scrollContainerRef}
                onToggle={toggleAction}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface PolicyServicePickerProps {
  options: Array<{ value: string; label: string }>;
  loading?: boolean;
  value?: string;
  onChange?: (value?: string) => void;
  onServiceChange: () => void;
}

function PolicyServicePicker({
  options,
  loading,
  value,
  onChange,
  onServiceChange,
}: PolicyServicePickerProps) {
  return (
    <Select
      allowClear
      showSearch
      optionFilterProp="label"
      loading={loading}
      value={value}
      options={options}
      placeholder="请选择服务"
      style={{ width: '100%' }}
      onChange={(nextValue) => {
        onChange?.(nextValue);
        onServiceChange();
      }}
    />
  );
}

interface PolicyActionsFieldProps {
  statementName: number;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  onActionsLoaded: (actions: PermissionAction[]) => void;
}

const PolicyActionsField = memo(function PolicyActionsField({
  statementName,
  scrollContainerRef,
  onActionsLoaded,
}: PolicyActionsFieldProps) {
  const form = Form.useFormInstance<PolicyFormValues>();
  const service = Form.useWatch(['visualStatements', statementName, 'service'], form);
  const actionQuery = useQuery({
    ...actionQueryOptions(service || ''),
    enabled: Boolean(service),
  });
  const actions = useMemo(
    () => (actionQuery.data ? toPermissionActions(actionQuery.data) : []),
    [actionQuery.data]
  );

  useEffect(() => {
    if (actions.length > 0) onActionsLoaded(actions);
  }, [actions, onActionsLoaded]);

  if (!service) {
    return <div className="iam-policy-actions-empty">选择服务后，此处将展开全部权限点</div>;
  }
  if (actionQuery.isLoading) {
    return <Skeleton active paragraph={{ rows: 3 }} />;
  }
  if (actionQuery.isError) {
    return (
      <Alert
        type="error"
        showIcon
        message="权限点加载失败"
        description={
          actionQuery.error instanceof Error ? actionQuery.error.message : String(actionQuery.error)
        }
      />
    );
  }

  return (
    <Form.Item
      name={[statementName, 'actions']}
      label="选择权限点"
      rules={[{ required: true, message: '至少选择一个权限点' }]}
    >
      <PolicyActionSelector actions={actions} scrollContainerRef={scrollContainerRef} />
    </Form.Item>
  );
});

export default function PolicyCreate() {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const [form] = Form.useForm<PolicyFormValues>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editorMode, setEditorMode] = useState<PolicyEditorMode>('visual');
  const [jsonValidationErrors, setJsonValidationErrors] = useState<string[]>([]);
  const [jsonValidationActive, setJsonValidationActive] = useState(false);
  const [convertingEditor, setConvertingEditor] = useState(false);
  const jsonValue = Form.useWatch('json', form);
  const editorScrollRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const serviceGroupsQuery = useQuery(actionResourcesQueryOptions());
  const actionsRef = useRef<PermissionAction[]>([]);
  const createPolicy = useCreateIamPolicy();
  const updatePolicy = useUpdateIamPolicy();

  useEffect(() => {
    let alive = true;
    (id
      ? iamHttpApi.getPolicy(id).then((response) => {
          if (response.code !== 0) {
            throw new Error(response.message || '获取权限策略详情失败');
          }
          return response;
        })
      : Promise.resolve(undefined)
    )
      .then((policyResponse) => {
        if (!alive) return;
        const policy = policyResponse?.data;
        if (policy && policy.policy_config.version !== '1') {
          throw new Error(`暂不支持策略版本 ${policy.policy_config.version}`);
        }
        const statements = policy
          ? policy.policy_config.statement.map(toEditorStatement)
          : INITIAL_STATEMENTS;
        form.setFieldsValue({
          name: policy?.name || '',
          description: policy?.description || '',
          visualStatements: statements.map((statement) => ({
            ...statement,
            service: policy?.permission_summary.actions.find((action) =>
              statement.actions.includes(action.code)
            )?.group_value,
          })),
          json: '',
        });
        setEditorMode('visual');
        setJsonValidationErrors([]);
        setJsonValidationActive(false);
      })
      .catch(() => undefined)
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [form, id, message]);

  const serviceOptions = useMemo(
    () => (serviceGroupsQuery.data ? toPolicyServiceOptions(serviceGroupsQuery.data) : []),
    [serviceGroupsQuery.data]
  );

  const registerPermissionActions = useCallback((nextActions: PermissionAction[]) => {
    const knownCodes = new Set(actionsRef.current.map((action) => action.code));
    if (nextActions.every((action) => knownCodes.has(action.code))) return;
    actionsRef.current = mergePermissionActionCatalogs(actionsRef.current, nextActions);
  }, []);

  const loadAllPermissionActions = async () => {
    const serviceGroups = await queryClient.fetchQuery(actionResourcesQueryOptions());
    const options = toPolicyServiceOptions(serviceGroups);
    const responses = await Promise.all(
      options.map(async ({ value }) => ({
        response: await queryClient.fetchQuery(actionQueryOptions(value)),
      }))
    );
    const nextActions = mergePermissionActionCatalogs(
      actionsRef.current,
      responses.flatMap(({ response }) => toPermissionActions(response))
    );
    actionsRef.current = nextActions;
    return nextActions;
  };

  useEffect(() => {
    if (editorMode !== 'json' || !jsonValidationActive) return;
    const timer = window.setTimeout(() => {
      setJsonValidationErrors(validatePolicyJson(jsonValue, actionsRef.current).errors);
    }, 280);
    return () => window.clearTimeout(timer);
  }, [editorMode, jsonValidationActive, jsonValue]);

  const restoreVisualConfiguration = () => {
    form.setFieldValue('json', '');
    setJsonValidationErrors([]);
    setJsonValidationActive(false);
    setEditorMode('visual');
  };

  const confirmDiscardJsonChanges = (errors: string[] = []) => {
    const invalid = errors.length > 0;
    modal.confirm({
      title: invalid ? 'JSON 配置校验不通过' : '放弃当前 JSON 修改？',
      content: invalid ? (
        <div className="iam-policy-json-switch-confirm">
          <Typography.Paragraph>当前内容无法转换为可视化配置：</Typography.Paragraph>
          <ul>
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
          <Typography.Paragraph>
            是否放弃当前 JSON 修改，并恢复切换前的可视化配置？
          </Typography.Paragraph>
        </div>
      ) : (
        '将恢复切换前的可视化配置，当前 JSON 内容不会保留。'
      ),
      okText: invalid ? '放弃并切换' : '放弃并恢复',
      okButtonProps: { danger: true },
      cancelText: '继续编辑 JSON',
      onOk: restoreVisualConfiguration,
    });
  };

  const switchEditorMode = async (nextMode: PolicyEditorMode) => {
    if (nextMode === editorMode || convertingEditor) return;

    setConvertingEditor(true);
    try {
      // 先让 loading 遮罩完成绘制，再执行同步 JSON 转换，避免大策略切换时页面无响应。
      await waitForEditorLoadingPaint();

      if (nextMode === 'json') {
        const statements = (form.getFieldValue('visualStatements') || []) as IamPolicyStatement[];
        form.setFieldValue('json', JSON.stringify(toPolicyConfig(statements), null, 2));
        setJsonValidationErrors([]);
        setJsonValidationActive(false);
        try {
          await loadAllPermissionActions();
        } catch {
          // 请求错误由全局请求层统一提示。
        }
        setEditorMode('json');
        return;
      }

      let availableActions: PermissionAction[];
      try {
        availableActions = await loadAllPermissionActions();
      } catch {
        return;
      }
      const result = validatePolicyJson(form.getFieldValue('json'), availableActions);
      if (result.errors.length || !result.statements) {
        setJsonValidationErrors(result.errors);
        setJsonValidationActive(true);
        confirmDiscardJsonChanges(result.errors);
        return;
      }
      form.setFieldValue(
        'visualStatements',
        toVisualStatements(result.statements, availableActions)
      );
      setJsonValidationErrors([]);
      setJsonValidationActive(false);
      setEditorMode('visual');
    } finally {
      setConvertingEditor(false);
    }
  };

  const formatJson = () => {
    setJsonValidationActive(true);
    try {
      const formatted = JSON.stringify(JSON.parse(form.getFieldValue('json')), null, 2);
      form.setFieldValue('json', formatted);
      setJsonValidationErrors(validatePolicyJson(formatted, actionsRef.current).errors);
      message.success('JSON 已格式化');
    } catch (error) {
      const errors = [`JSON 语法：${error instanceof Error ? error.message : '无法解析当前内容'}`];
      setJsonValidationErrors(errors);
      message.error('JSON 语法错误，暂时无法格式化');
    }
  };

  const submit = async () => {
    const values = await form.validateFields();
    let statements = values.visualStatements as IamPolicyStatement[];
    if (editorMode === 'json') {
      let availableActions: PermissionAction[];
      try {
        availableActions = await loadAllPermissionActions();
      } catch {
        return;
      }
      const validation = validatePolicyJson(values.json, availableActions);
      if (validation.errors.length || !validation.statements) {
        setJsonValidationErrors(validation.errors);
        setJsonValidationActive(true);
        message.error(validation.errors[0] || '策略配置校验失败');
        return;
      }
      statements = validation.statements;
    }

    const execute = async () => {
      setSubmitting(true);
      try {
        if (editing && id) {
          await updatePolicy.mutateAsync({
            policy_id: id,
            name: values.name,
            description: values.description ?? '',
            policy_config: toPolicyConfig(statements),
          });
          message.success('策略已更新');
          navigate(`/iam/policies/${id}`, { replace: true });
        } else {
          await createPolicy.mutateAsync({
            name: values.name,
            description: values.description ?? '',
            policy_config: toPolicyConfig(statements),
          });
          message.success('策略已创建');
          navigate('/iam/policies', { replace: true });
        }
      } finally {
        setSubmitting(false);
      }
    };
    if (editing) {
      modal.confirm({
        title: '确认更新权限策略？',
        content: '更新后将立即影响所有关联用户和用户组的权限判定。',
        okText: '确认更新',
        onOk: execute,
      });
    } else {
      await execute();
    }
  };

  if (loading) {
    return (
      <div className="pp-page">
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  return (
    <div className="pp-page iam-page iam-policy-page iam-policy-create-page">
      <header className="iam-policy-page-head">
        <Button
          type="text"
          className="iam-policy-detail-back"
          aria-label="返回权限策略列表"
          icon={<ChevronLeft size={16} />}
          onClick={() => navigate('/iam/policies')}
        />
        <div className="iam-policy-page-title">
          <h2>{editing ? '编辑权限策略' : '新建权限策略'}</h2>
        </div>
        <Space>
          <Button onClick={() => navigate('/iam/policies')}>取消</Button>
          <Button
            type="primary"
            icon={<Save size={14} />}
            loading={submitting}
            disabled={convertingEditor}
            onClick={submit}
          >
            {editing ? '保存更新' : '创建策略'}
          </Button>
        </Space>
      </header>

      <div className="iam-policy-page-content">
        {serviceGroupsQuery.isError && (
          <Alert
            type="error"
            showIcon
            message="服务列表加载失败"
            description={
              serviceGroupsQuery.error instanceof Error
                ? serviceGroupsQuery.error.message
                : String(serviceGroupsQuery.error)
            }
          />
        )}
        <Form form={form} layout="vertical" className="iam-policy-editor-form">
          <div className="iam-policy-basic-fields">
            <Form.Item
              name="name"
              label="策略名称"
              rules={[{ required: true, message: '请输入策略名称' }]}
            >
              <Input placeholder="例如 AgentOpsReadOnly" />
            </Form.Item>
            <Form.Item name="description" label="描述">
              <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 4 }}
                placeholder="说明策略适用场景"
              />
            </Form.Item>
          </div>
          <Form.Item className="iam-policy-mode-switch">
            <Radio.Group
              value={editorMode}
              disabled={convertingEditor}
              optionType="button"
              buttonStyle="solid"
              onChange={(event) => void switchEditorMode(event.target.value)}
            >
              <Radio.Button value="visual">可视化</Radio.Button>
              <Radio.Button value="json">JSON</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Spin
            spinning={convertingEditor}
            size="large"
            tip={editorMode === 'visual' ? '正在转换为 JSON…' : '正在转换为可视化配置…'}
            wrapperClassName="iam-policy-editor-spin"
          >
            <div ref={editorScrollRef} className="iam-policy-editor-switching">
              {editorMode === 'json' ? (
                <div className="iam-policy-json-panel">
                  {jsonValidationErrors.length > 0 && (
                    <Alert
                      className="iam-policy-json-error"
                      type="error"
                      showIcon
                      message="JSON 配置校验不通过"
                      description={
                        <ul className="iam-policy-json-error-list">
                          {jsonValidationErrors.map((error) => (
                            <li key={error}>{error}</li>
                          ))}
                        </ul>
                      }
                    />
                  )}
                  <Alert
                    className="iam-policy-json-guide"
                    type="info"
                    showIcon
                    message="JSON 配置说明"
                    description={
                      <div className="iam-policy-json-guide-copy">
                        <Typography.Paragraph>
                          此处内容会直接作为创建接口的 <code>policy_config</code> 提交，字段名与接口
                          保持一致。
                        </Typography.Paragraph>
                        <Typography.Text strong>校验规则</Typography.Text>
                        <ul>
                          <li>
                            <code>version</code> 固定为字符串 <code>&quot;1&quot;</code>。
                          </li>
                          <li>
                            <code>statement</code> 必须为非空数组。
                          </li>
                          <li>
                            每条 <code>statement</code> 仅支持 <code>effect</code>、
                            <code>action</code> 和可选的 <code>condition</code>。
                          </li>
                          <li>
                            <code>effect</code> 仅支持 <code>Allow</code> 或 <code>Deny</code>。
                          </li>
                          <li>
                            <code>action</code> 必须为平台已定义的权限 code 或单独配置的服务通配符。
                          </li>
                          <li>
                            一条 <code>statement</code> 只能包含一个服务；多个服务需拆分为多条。
                          </li>
                          <li>
                            “我创建的资源”必须传固定的 <code>condition.string_equals</code>：
                            <code>inf:resourceOwner = &quot;{'{self}'}&quot;</code>
                            ；“租户全部资源”不传 <code>condition</code>。
                          </li>
                        </ul>
                        <Typography.Text type="secondary">
                          若 JSON 未通过校验，将停留在当前模式并保留内容，修正后可继续切换。
                        </Typography.Text>
                      </div>
                    }
                  />
                  <div className="iam-policy-json-toolbar">
                    <Button icon={<Braces size={14} />} onClick={formatJson}>
                      格式化 JSON
                    </Button>
                    <Button
                      icon={<RotateCcw size={14} />}
                      onClick={() => confirmDiscardJsonChanges()}
                    >
                      放弃 JSON 修改
                    </Button>
                  </div>
                  <Form.Item
                    name="json"
                    label="平台策略 JSON"
                    rules={[{ required: true, message: '请输入策略 JSON' }]}
                  >
                    <Input.TextArea
                      className="iam-policy-json"
                      rows={18}
                      spellCheck={false}
                      onChange={() => setJsonValidationActive(true)}
                    />
                  </Form.Item>
                </div>
              ) : (
                <Form.List name="visualStatements">
                  {(fields, { add, remove }) => (
                    <div className="iam-policy-statements">
                      {fields.map((field, index) => (
                        <div className="iam-policy-statement-editor" key={field.key}>
                          <div className="iam-policy-statement-head">
                            <Typography.Text strong>服务配置 {index + 1}</Typography.Text>
                            {fields.length > 1 && (
                              <Button
                                type="text"
                                danger
                                icon={<Trash2 size={14} />}
                                onClick={() => remove(field.name)}
                              >
                                移除
                              </Button>
                            )}
                          </div>
                          <div className="iam-policy-rule-row">
                            <Form.Item
                              name={[field.name, 'effect']}
                              label="权限类型"
                              rules={[{ required: true }]}
                            >
                              <Radio.Group
                                options={[
                                  { value: 'Allow', label: '允许' },
                                  { value: 'Deny', label: '拒绝' },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[field.name, 'resourceScope']}
                              label="资源范围"
                              rules={[{ required: true }]}
                            >
                              <Radio.Group
                                options={[
                                  { value: 'OWN', label: '我创建的资源' },
                                  { value: 'ALL', label: '租户全部资源' },
                                ]}
                              />
                            </Form.Item>
                          </div>
                          <Form.Item
                            name={[field.name, 'service']}
                            label="选择服务"
                            rules={[{ required: true, message: '请选择服务' }]}
                          >
                            <PolicyServicePicker
                              options={serviceOptions}
                              loading={serviceGroupsQuery.isLoading}
                              onServiceChange={() => {
                                form.setFieldValue(['visualStatements', field.name, 'actions'], []);
                              }}
                            />
                          </Form.Item>
                          <PolicyActionsField
                            statementName={field.name}
                            scrollContainerRef={editorScrollRef}
                            onActionsLoaded={registerPermissionActions}
                          />
                        </div>
                      ))}
                      <Button
                        type="dashed"
                        block
                        icon={<Plus size={15} />}
                        onClick={() =>
                          add({
                            service: undefined,
                            effect: 'Allow',
                            resourceScope: 'OWN',
                            actions: [],
                          })
                        }
                      >
                        添加服务配置
                      </Button>
                    </div>
                  )}
                </Form.List>
              )}
            </div>
          </Spin>
        </Form>
      </div>
    </div>
  );
}
